import React, { useMemo, useState, useCallback } from 'react';
import { Modal, Form } from 'react-bootstrap';
import { FormGroup } from '../../../../../../components/form/FormGroup';
import InputLabel from '../../../../../../components/form/InputLabel';
import Button from '../../../../../../components/button';
import { useTranslation } from 'react-i18next';
import { useMutationErrorHandler } from '../../../../../../hooks/useMutationErrorHandler';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { useAvailableGoalsQuery } from '../../../../__generated__/graphql';
import ServerError from '../../../../../../components/ServerError';
import { mapGoalOption } from '../useGoalSelectOptions';
import { SelectSingleGoal } from '../GoalSidebar/SelectSingleGoal';
import { GoalId } from '../types';
import { useSelectedGoal } from '../useSelectedGoal';
import SingleFilter from '../../../../../../components/SelectFilters/SingleFilter';
import { toObjectiveIdInput } from '../mappings';
import { useAvailableGoalsFilter } from '../useAvailableGoalsFilter';
import '../GoalsModal.css';
import { usePullObjectives } from '../GoalSidebar/useObjectiveMutations';

type PullGoalModalProps = {
  visible: boolean;
  onClose: () => void;
};

const i18prefix = 'performanceReview.goals2.pullGoal';

export const PullGoalModal: React.FC<PullGoalModalProps> = ({
  visible,
  onClose,
}) => {
  const { t } = useTranslation();
  const errorHandler = useMutationErrorHandler();
  const { currentPeriodId } = useGoalsContext();
  const [selectedGoalId, setSelectedGoalId] = useState<GoalId | null>(null);
  const [setAsParent, setSetAsParent] = useState<boolean>(true);
  const [pullObjectives, { loading: isPulling }] = usePullObjectives();
  const { setSelectedGoalId: openGoalSidebar } = useSelectedGoal();
  const { data, error, loading } = useAvailableGoalsQuery({
    variables: {
      periodId: currentPeriodId,
    },
    skip: !visible,
  });

  const availableGoals = useMemo(() => {
    if (!data?.allObjectives) {
      return [];
    }
    return data.allObjectives.map(mapGoalOption);
  }, [data?.allObjectives]);

  const {
    filterMode,
    filterModeOptions,
    filteredGoals: availableGoalsForPulling,
    handleFilterModeChange,
    resetFilterMode,
  } = useAvailableGoalsFilter({
    allGoals: availableGoals,
    setSelectedGoalId,
  });

  const handleClose = useCallback(() => {
    setSelectedGoalId(null);
    setSetAsParent(true);
    resetFilterMode();
    onClose();
  }, [onClose, resetFilterMode]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedGoalId) {
        return;
      }
      pullObjectives({
        variables: {
          input: {
            objectiveIds: [toObjectiveIdInput(selectedGoalId)],
            setAsParent,
          },
        },
      })
        .then((result) => {
          const createdRecordIds =
            result.data?.objectives?.pullObjectives?.createdRecordIds;
          if (createdRecordIds && createdRecordIds.length === 1) {
            const createdGoalId = createdRecordIds[0];
            // it is redirect
            openGoalSidebar({ id: createdGoalId, type: 'employee' });
          }
          handleClose();
        })
        .catch(errorHandler);
    },
    [
      selectedGoalId,
      setAsParent,
      pullObjectives,
      handleClose,
      openGoalSidebar,
      errorHandler,
    ],
  );

  if (!visible) {
    return null;
  }

  return (
    <Modal
      show={visible}
      onHide={handleClose}
      size="lg"
      centered
      backdropClassName="goals-modal-backdrop"
      contentClassName="goals-modal-content border">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title>{t(`${i18prefix}.title`)}</Modal.Title>
            <div>{t(`${i18prefix}.subtitle`)}</div>
          </div>
        </Modal.Header>

        <Modal.Body className="p-4">
          {error ? (
            <ServerError />
          ) : (
            <>
              <FormGroup>
                <InputLabel label={t(`${i18prefix}.filterLabel`)} />
                <SingleFilter
                  placeholder={t(`${i18prefix}.filterPlaceholder`)}
                  options={filterModeOptions}
                  value={filterMode}
                  onChange={handleFilterModeChange}
                  isClearable={false}
                />
              </FormGroup>
              <FormGroup>
                <InputLabel label={t(`${i18prefix}.selectGoalLabel`)} />
                <SelectSingleGoal
                  value={selectedGoalId}
                  options={availableGoalsForPulling}
                  placeholder={t(`${i18prefix}.selectGoalPlaceholder`)}
                  isDisabled={loading}
                  onChange={setSelectedGoalId}
                />
              </FormGroup>
              <FormGroup>
                <Form.Check
                  type="checkbox"
                  id="setAsParent"
                  label={t(`${i18prefix}.setAsParent`)}
                  checked={setAsParent}
                  onChange={(e) => setSetAsParent(e.target.checked)}
                />
                <Form.Text className="text-muted">
                  {setAsParent
                    ? t(`${i18prefix}.copyDescriptionWithParent`)
                    : t(`${i18prefix}.copyDescriptionWithoutParent`)}
                </Form.Text>
              </FormGroup>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            label={t(`${i18prefix}.cancel`)}
            onClick={handleClose}
            disabled={isPulling}
          />
          {!error && (
            <Button
              variant="primary"
              label={t(`${i18prefix}.pullGoal`)}
              type="submit"
              isProcessing={isPulling}
              disabled={isPulling || loading || !selectedGoalId}
            />
          )}
        </Modal.Footer>
      </form>
    </Modal>
  );
};
